// ============================================================
// DashboardTab.jsx  —  Noble Enterprises (tape module)
//
// Quantities for everyone. Money cards only for the owner —
// /dashboard/kpis already strips them for operators, so the
// UI just renders whatever comes back.
//
// Data:
//   /tape/dashboard/kpis     cards
//   /tape/cut-runs/stock     finished stock by spec
//   /tape/sales              last 5 invoices
// ============================================================

import React, { useState, useEffect } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const C = {
  text: '#1a2332', textSoft: '#64748b', textMuted: '#94a3b8',
  border: '#e2e8f0', borderSoft: '#f1f5f9', rowAlt: '#fafbfc',
  brand: '#f39c12', info: '#0284c7', success: '#059669', danger: '#dc2626',
};

const fmtRs  = (n) => `Rs ${Number(n || 0).toLocaleString('en-PK', { maximumFractionDigits: 0 })}`;
const fmtNum = (n) => Number(n || 0).toLocaleString('en-PK', { maximumFractionDigits: 2 });
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString('en-GB') : '—');

const Card = ({ label, value, sub, color = C.text }) => (
  <div style={{
    background: '#fff', border: `1px solid ${C.border}`, borderRadius: '10px',
    padding: '16px 18px', borderLeft: `4px solid ${color}`,
  }}>
    <div style={{ fontSize: '11px', color: C.textMuted, fontWeight: '600', textTransform: 'uppercase' }}>{label}</div>
    <div style={{ fontSize: '22px', fontWeight: '700', color, marginTop: '4px' }}>{value}</div>
    {sub && <div style={{ fontSize: '12px', color: C.textSoft, marginTop: '2px' }}>{sub}</div>}
  </div>
);

const Panel = ({ title, children }) => (
  <div style={{
    background: '#fff', border: `1px solid ${C.border}`, borderRadius: '12px',
    overflow: 'hidden', marginBottom: '20px',
  }}>
    <div style={{ padding: '14px 18px', borderBottom: `1px solid ${C.borderSoft}` }}>
      <h3 style={{ margin: 0, fontSize: '15px', fontWeight: '700', color: C.text }}>{title}</h3>
    </div>
    <div style={{ overflowX: 'auto' }}>{children}</div>
  </div>
);

const th = {
  textAlign: 'left', padding: '10px 18px', fontSize: '12px', fontWeight: '600',
  color: C.textSoft, background: C.rowAlt, textTransform: 'uppercase',
};
const td = { padding: '11px 18px', fontSize: '14px', color: C.text, borderTop: `1px solid ${C.borderSoft}` };
const right = { textAlign: 'right' };

const Empty = ({ cols, text }) => (
  <tr><td colSpan={cols} style={{ ...td, textAlign: 'center', color: C.textMuted, padding: '30px' }}>{text}</td></tr>
);

const DashboardTab = () => {
  const [kpi, setKpi]         = useState(null);
  const [stock, setStock]     = useState([]);
  const [invoices, setInv]    = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState('');

  useEffect(() => {
    const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };
    Promise.allSettled([
      axios.get(`${API}/tape/dashboard/kpis`, { headers }),
      axios.get(`${API}/tape/cut-runs/stock`, { headers }),
      axios.get(`${API}/tape/sales`, { headers }),
    ]).then(([k, s, i]) => {
      if (k.status === 'fulfilled') setKpi(k.value.data.data);
      else setError('Could not load dashboard figures.');
      if (s.status === 'fulfilled') setStock(s.value.data.data?.stock || []);
      if (i.status === 'fulfilled') setInv((i.value.data.data || []).slice(0, 5));
      setLoading(false);
    });
  }, []);

  if (loading) {
    return <div style={{ textAlign: 'center', padding: '60px', color: C.textMuted }}>Loading dashboard…</div>;
  }

  const hasMoney = kpi && kpi.net_profit !== undefined;
    const profit   = Number(kpi?.net_profit || 0);

  // One row per size. Stock sits at the factory and the shop as
  // separate rows; here they are added together. Price is the
  // shop's, since that is the only place anything is sold from.
  const combined = Object.values(stock.reduce((acc, r) => {
    const key = [r.tape_type_name, r.micron_value, r.width_mm, r.length_yards].join('|');
    if (!acc[key]) acc[key] = { ...r, key, quantity: 0, price: null };
    acc[key].quantity += Number(r.quantity || 0);
    if (Number(r.price) > 0 && (r.location === 'shop' || acc[key].price == null)) {
      acc[key].price = r.price;
    }
    return acc;
  }, {}));

  return (
    <div>
      <h2 style={{ fontSize: '20px', fontWeight: '700', color: C.text, margin: '0 0 18px' }}>
        Adhesive Tapes Overview
      </h2>

      {error && (
        <div style={{ background: '#fee2e2', color: C.danger, padding: '11px 16px', borderRadius: '8px', marginBottom: '16px', fontSize: '14px' }}>
          {error}
        </div>
      )}

      {kpi && (
        <div style={{
          display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
          gap: '14px', marginBottom: '20px',
        }}>
                    <Card label="Cores in stock"  value={fmtNum(kpi.cores_in_stock)} color={C.text} />

          {hasMoney && (
            <>
              <Card label="Stock value"        value={fmtRs(kpi.stock_value)} color={C.textSoft} />
              <Card label="Revenue (month)"    value={fmtRs(kpi.revenue)}     color={C.success} />
              <Card label="Expenses (month)"   value={fmtRs(kpi.expenditure)} color={C.danger} />
              <Card
                label="Net profit (month)"
                value={`${profit < 0 ? '−' : ''}${fmtRs(Math.abs(profit))}`}
                sub="Full breakdown in P&L report"
                color={profit >= 0 ? C.success : C.danger}
              />
            </>
          )}
        </div>
      )}

      <Panel title="Finished stock">
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={th}>Type</th>
              <th style={{ ...th, ...right }}>Micron</th>
              <th style={{ ...th, ...right }}>Width (mm)</th>
              <th style={{ ...th, ...right }}>Length (yd)</th>
              <th style={{ ...th, ...right }}>Pieces</th>
              <th style={{ ...th, ...right }}>Price</th>
            </tr>
          </thead>
          <tbody>
                        {combined.length === 0 && <Empty cols={6} text="No finished stock." />}
            {combined.map(r => (
              <tr key={r.key}>
                <td style={{ ...td, fontWeight: '600' }}>{r.tape_type_name}</td>
                <td style={{ ...td, ...right }}>{fmtNum(r.micron_value)}</td>
                <td style={{ ...td, ...right }}>{fmtNum(r.width_mm)}</td>
                <td style={{ ...td, ...right }}>{fmtNum(r.length_yards)}</td>
                <td style={{ ...td, ...right, fontWeight: '700', color: Number(r.quantity) > 0 ? C.text : C.textMuted }}>
                  {fmtNum(r.quantity)}
                </td>
                <td style={{ ...td, ...right }}>{r.price != null ? fmtRs(r.price) : '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Panel>

      <Panel title="Recent invoices">
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={th}>Invoice</th>
              <th style={th}>Customer</th>
              <th style={th}>Date</th>
              <th style={{ ...th, ...right }}>Pieces</th>
              <th style={{ ...th, ...right }}>Total</th>
              <th style={{ ...th, ...right }}>Balance</th>
              <th style={th}>Status</th>
            </tr>
          </thead>
          <tbody>
            {invoices.length === 0 && <Empty cols={7} text="No invoices yet." />}
            {invoices.map(inv => (
              <tr key={inv.id}>
                <td style={{ ...td, fontWeight: '600' }}>{inv.invoice_number}</td>
                <td style={td}>{inv.customer_name || '—'}</td>
                <td style={td}>{fmtDate(inv.invoice_date)}</td>
                <td style={{ ...td, ...right }}>{fmtNum(inv.total_pieces)}</td>
                <td style={{ ...td, ...right }}>{fmtRs(inv.grand_total)}</td>
                <td style={{ ...td, ...right, color: Number(inv.balance) > 0 ? C.danger : C.textMuted }}>
                  {fmtRs(inv.balance)}
                </td>
                <td style={{ ...td, textTransform: 'capitalize' }}>{inv.payment_status || '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Panel>
    </div>
  );
};

export default DashboardTab;